import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "../Orders/Order.css";
import "./Shipments.css";

const API_URL = process.env.REACT_APP_API_URL;

// Shipment endpoints are admin-only: send the stored JWT on every request.
const authConfig = (extra = {}) => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
  ...extra,
});

const LABEL_SIZES = [
  ["SHIP_LABEL_A4", "A4"],
  ["SHIP_LABEL_A6", "A6"],
  ["SHIP_LABEL_4X6", "4x6 (thermal)"],
];
const EMPTY_PARCEL = { weight: "", length: "", width: "", height: "" };

// Label downloads come back as a Blob even when they fail, so unwrap the JSON first.
const errMsg = async (err) => {
  let data = err.response && err.response.data;
  if (data instanceof Blob) {
    try {
      data = JSON.parse(await data.text());
    } catch (e) {
      data = null;
    }
  }
  return (data && data.message) || err.message || "Something went wrong";
};

// Student-typed text going into Swal's html string must be escaped.
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const ADDRESS_FIELDS = [
  ["name", "Name"],
  ["phone", "Phone (10 digits)"],
  ["addressLine", "Address"],
  ["city", "City"],
  ["state", "State"],
  ["postalCode", "Pincode (6 digits)"],
];

const fmt = (d) => (d ? new Date(d).toLocaleString("en-IN") : "-");
const itemsText = (o) => o.Items.map((i) => `${i.name} (x${i.quantity})`).join(", ");

const Shipments = () => {
  const [tab, setTab] = useState("toship");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [parcels, setParcels] = useState({});
  const [lastParcel, setLastParcel] = useState(EMPTY_PARCEL); // pre-fills the next rows
  const [busy, setBusy] = useState({});
  const [labelCode, setLabelCode] = useState("SHIP_LABEL_A4");
  const [trackView, setTrackView] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/shipment/admin/orders?tab=${tab}`, authConfig());
      setOrders(res.data);
    } catch (err) {
      setOrders([]);
      Swal.fire({ icon: "error", title: "Could not load shipments", text: await errMsg(err) });
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  // Disables a row's buttons while its request is in flight and reports failures.
  const run = async (id, fn) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await fn();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed", text: await errMsg(err) });
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const parcelOf = (id) => parcels[id] || lastParcel;
  const setParcel = (id, field, value) =>
    setParcels((p) => ({ ...p, [id]: { ...(p[id] || lastParcel), [field]: value } }));

  const book = (o) =>
    run(o._id, async () => {
      const p = parcelOf(o._id);
      if (!p.weight || !p.length || !p.width || !p.height) {
        throw new Error("Enter weight, length, width and height first");
      }
      const res = await axios.post(`${API_URL}/shipment/admin/book/${o._id}`, p, authConfig());
      setLastParcel(p);
      setOrders((list) => list.filter((x) => x._id !== o._id));
      Swal.fire({ icon: "success", title: "Booked", text: `AWB ${res.data.shipment.awb}`, timer: 2500 });
    });

  const editAddress = async (o) => {
    const result = await Swal.fire({
      title: "Edit shipping address",
      html: ADDRESS_FIELDS.map(
        ([key, label]) =>
          `<label style="display:block;text-align:left;margin-top:8px">${label}<input id="addr-${key}" class="swal2-input" style="margin:2px 0;width:100%" value="${esc(o.Address[key])}"></label>`
      ).join(""),
      showCancelButton: true,
      confirmButtonText: "Save",
      preConfirm: () => Object.fromEntries(ADDRESS_FIELDS.map(([key]) => [key, document.getElementById(`addr-${key}`).value])),
    });
    if (!result.isConfirmed) return;
    run(o._id, async () => {
      const res = await axios.post(`${API_URL}/shipment/admin/address/${o._id}`, result.value, authConfig());
      setOrders((list) => list.map((x) => (x._id === o._id ? res.data : x)));
    });
  };

  const cancel = async (o) => {
    const confirmation = await Swal.fire({
      title: `Cancel AWB ${o.shipment.awb}?`,
      text: "This cancels the shipment with DTDC. The order goes back to To ship.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, cancel it",
      cancelButtonText: "Keep it",
    });
    if (!confirmation.isConfirmed) return;
    run(o._id, async () => {
      await axios.post(`${API_URL}/shipment/admin/cancel/${o._id}`, {}, authConfig());
      setOrders((list) => list.filter((x) => x._id !== o._id));
    });
  };

  const label = (o) => {
    // Open the tab now, inside the click, so popup blockers allow it; fill it once the PDF arrives.
    const w = window.open("", "_blank");
    run(o._id, async () => {
      try {
        const res = await axios.get(
          `${API_URL}/shipment/admin/label/${o._id}?code=${labelCode}`,
          authConfig({ responseType: "blob" })
        );
        const url = URL.createObjectURL(res.data);
        if (w) {
          w.location.href = url;
        } else {
          const a = document.createElement("a");
          a.href = url;
          a.download = `${o.shipment.awb}.pdf`;
          a.click();
        }
      } catch (err) {
        if (w) w.close();
        throw err;
      }
    });
  };

  const track = (o) =>
    run(o._id, async () => {
      const res = await axios.get(`${API_URL}/shipment/track/${o._id}`, authConfig());
      setTrackView(res.data);
      setOrders((list) =>
        list.map((x) =>
          x._id === o._id
            ? { ...x, shipment: { ...x.shipment, dtdcStatus: res.data.status, trackedAt: new Date().toISOString() } }
            : x
        )
      );
    });

  const refreshAll = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/shipment/admin/refresh`, {}, authConfig());
      Swal.fire({
        icon: "success",
        title: "Statuses refreshed",
        text: `${res.data.updated} updated, ${res.data.failed} failed`,
        timer: 2500,
      });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Refresh failed", text: await errMsg(err) });
    }
    await load();
  };

  const shipTo = (o) => (
    <>
      <strong>{o.Address.name}</strong> ({o.Address.phone})
      <br />
      {`${o.Address.addressLine}, ${o.Address.city}, ${o.Address.state} ${o.Address.postalCode}`}
      <br />
      <small>Student {o.studentId}</small>
    </>
  );

  return (
    <div className="admin-materials-container">
      <h1>DTDC Shipments</h1>

      <div className="shipments-toolbar">
        <button
          className={`shipments-tab ${tab === "toship" ? "active" : ""}`}
          onClick={() => setTab("toship")}
        >
          To ship
        </button>
        <button
          className={`shipments-tab ${tab === "booked" ? "active" : ""}`}
          onClick={() => setTab("booked")}
        >
          Booked
        </button>

        {tab === "booked" && (
          <>
            <label className="shipments-label-size">
              Label size:&nbsp;
              <select value={labelCode} onChange={(e) => setLabelCode(e.target.value)}>
                {LABEL_SIZES.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <button className="admin-materials-btn-primary" onClick={refreshAll} disabled={loading}>
              Refresh statuses
            </button>
          </>
        )}
      </div>

      <div className="admin-materials-table-wrapper">
        <table className="admin-materials-table">
          <thead>
            {tab === "toship" ? (
              <tr>
                <th>Order date</th>
                <th>Ship to</th>
                <th>Items</th>
                <th>Total</th>
                <th>Parcel (kg / cm)</th>
                <th>Action</th>
              </tr>
            ) : (
              <tr>
                <th>AWB</th>
                <th>Booked on</th>
                <th>Ship to</th>
                <th>Items</th>
                <th>DTDC status</th>
                <th>Actions</th>
              </tr>
            )}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6">Loading...</td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-results">
                  {tab === "toship" ? "No paid orders waiting to be shipped" : "No booked shipments"}
                </td>
              </tr>
            ) : tab === "toship" ? (
              orders.map((o) => {
                const p = parcelOf(o._id);
                const cancelled = (o.shipment && o.shipment.cancelledAwbs) || [];
                return (
                  <tr key={o._id}>
                    <td>{fmt(o.purchasedDate)}</td>
                    <td>
                      {shipTo(o)}
                      <br />
                      <button className="shipments-link" disabled={busy[o._id]} onClick={() => editAddress(o)}>
                        Edit address
                      </button>
                      {cancelled.length > 0 && (
                        <div className="shipments-note">Cancelled AWB: {cancelled.join(", ")}</div>
                      )}
                    </td>
                    <td>{itemsText(o)}</td>
                    <td>₹{o.totalAmount}</td>
                    <td>
                      <div className="shipments-parcel">
                        {[
                          ["weight", "Wt kg"],
                          ["length", "L"],
                          ["width", "W"],
                          ["height", "H"],
                        ].map(([field, ph]) => (
                          <input
                            key={field}
                            type="number"
                            min="0"
                            step="any"
                            placeholder={ph}
                            title={field}
                            value={p[field]}
                            onChange={(e) => setParcel(o._id, field, e.target.value)}
                          />
                        ))}
                      </div>
                    </td>
                    <td>
                      <button
                        className="admin-materials-btn-primary"
                        disabled={busy[o._id]}
                        onClick={() => book(o)}
                      >
                        {busy[o._id] ? "Booking..." : "Book DTDC"}
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              orders.map((o) => (
                <tr key={o._id}>
                  <td>
                    <strong>{o.shipment.awb}</strong>
                  </td>
                  <td>{fmt(o.shipment.bookedAt)}</td>
                  <td>{shipTo(o)}</td>
                  <td>{itemsText(o)}</td>
                  <td>
                    {o.shipment.dtdcStatus || "Booked"}
                    <br />
                    <small>{o.shipment.trackedAt ? `checked ${fmt(o.shipment.trackedAt)}` : "not checked yet"}</small>
                  </td>
                  <td>
                    <div className="shipments-actions">
                      <button className="admin-materials-btn-primary" disabled={busy[o._id]} onClick={() => label(o)}>
                        Label
                      </button>
                      <button className="admin-materials-btn-primary" disabled={busy[o._id]} onClick={() => track(o)}>
                        Track
                      </button>
                      <button className="admin-materials-btn-danger" disabled={busy[o._id]} onClick={() => cancel(o)}>
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {trackView && (
        <div className="shipments-modal-overlay" onClick={() => setTrackView(null)}>
          <div className="shipments-modal" onClick={(e) => e.stopPropagation()}>
            <h2>AWB {trackView.awb}</h2>
            <p>
              <strong>{trackView.status}</strong>
              {trackView.expectedDelivery && ` - expected ${new Date(trackView.expectedDelivery).toLocaleDateString("en-IN")}`}
            </p>
            {trackView.timeline.length === 0 ? (
              <p>No scans yet. Updates appear after DTDC picks the parcel up.</p>
            ) : (
              <ul className="shipments-timeline">
                {trackView.timeline.map((t, i) => (
                  <li key={i}>
                    <strong>{t.action}</strong> {t.location && `- ${t.location}`}
                    <br />
                    <small>
                      {fmt(t.at)}
                      {t.remarks && ` - ${t.remarks}`}
                    </small>
                  </li>
                ))}
              </ul>
            )}
            <button className="admin-materials-btn-primary" onClick={() => setTrackView(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shipments;
